/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { prettyDuration, showPrettyDuration, commonDurationRanges } from './pretty_duration';
import { prettyInterval } from './pretty_interval';
import dateMath from '@elastic/datemath';
import { EuiSuperUpdateButton } from './super_update_button';
import { EuiQuickSelectPopover } from './quick_select_popover/quick_select_popover';
import { EuiDatePopoverButton } from './date_popover/date_popover_button';
import { EuiDatePickerRange } from '../date_picker_range';
import { EuiFormControlLayout } from '../../form';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { AsyncInterval } from './async_interval';
import { EuiI18n } from '../../i18n';
import { EuiI18nConsumer } from '../../context'; // eslint-disable-line import/named

export { prettyDuration, commonDurationRanges };

function isRangeInvalid(start, end) {
  if (start === 'now' && end === 'now') {
    return true;
  }

  var startMoment = dateMath.parse(start);
  var endMoment = dateMath.parse(end, {
    roundUp: true
  });

  if (!startMoment || !endMoment || !startMoment.isValid() || !endMoment.isValid()) {
    return true;
  }

  if (startMoment.isAfter(endMoment)) {
    return true;
  }

  return false;
}

export class EuiSuperDatePicker extends Component {
  static defaultProps = {
    commonlyUsedRanges: commonDurationRanges,
    dateFormat: 'MMM D, YYYY @ HH:mm:ss.SSS',
    end: 'now',
    isAutoRefreshOnly: false,
    isDisabled: false,
    isPaused: true,
    recentlyUsedRanges: [],
    refreshInterval: 0,
    showUpdateButton: true,
    start: 'now-15m',
    timeFormat: 'HH:mm'
  };
  state = {
    prevProps: {
      start: this.props.start,
      end: this.props.end
    },
    start: this.props.start,
    end: this.props.end,
    isInvalid: isRangeInvalid(this.props.start, this.props.end),
    hasChanged: false,
    showPrettyDuration: showPrettyDuration(this.props.start, this.props.end, this.props.commonlyUsedRanges),
    isStartDatePopoverOpen: false,
    isEndDatePopoverOpen: false
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.start !== prevState.prevProps.start || nextProps.end !== prevState.prevProps.end) {
      return {
        prevProps: {
          start: nextProps.start,
          end: nextProps.end
        },
        start: nextProps.start,
        end: nextProps.end,
        isInvalid: isRangeInvalid(nextProps.start, nextProps.end),
        hasChanged: false,
        showPrettyDuration: showPrettyDuration(nextProps.start, nextProps.end, nextProps.commonlyUsedRanges)
      };
    }

    return null;
  }

  setTime = ({
    end,
    start
  }) => {
    const isInvalid = isRangeInvalid(start, end);
    this.setState({
      start,
      end,
      isInvalid,
      hasChanged: true
    });

    if (!this.props.showUpdateButton) {
      this.props.onTimeChange({
        start,
        end,
        isQuickSelection: false,
        isInvalid
      });
    }
  };
  componentDidMount = () => {
    if (!this.props.isPaused) {
      this.startInterval(this.props.refreshInterval);
    }
  };
  componentDidUpdate = () => {
    this.stopInterval();

    if (!this.props.isPaused) {
      this.startInterval(this.props.refreshInterval);
    }
  };
  componentWillUnmount = () => {
    this.stopInterval();
  };
  setStart = start => {
    this.setTime({
      start,
      end: this.state.end
    });
  };
  setEnd = end => {
    this.setTime({
      start: this.state.start,
      end
    });
  };
  applyTime = () => {
    this.props.onTimeChange({
      start: this.state.start,
      end: this.state.end,
      isQuickSelection: false,
      isInvalid: false
    });
  };
  applyQuickTime = ({
    start,
    end
  }) => {
    this.setState({
      showPrettyDuration: showPrettyDuration(start, end, commonDurationRanges)
    });
    this.props.onTimeChange({
      start,
      end,
      isQuickSelection: true,
      isInvalid: false
    });
  };
  hidePrettyDuration = () => {
    this.setState({
      showPrettyDuration: false
    });
  };
  onStartDatePopoverToggle = () => {
    this.setState(prevState => {
      return {
        isStartDatePopoverOpen: !prevState.isStartDatePopoverOpen
      };
    });
  };
  onStartDatePopoverClose = () => {
    this.setState({
      isStartDatePopoverOpen: false
    });
  };
  onEndDatePopoverToggle = () => {
    this.setState(prevState => {
      return {
        isEndDatePopoverOpen: !prevState.isEndDatePopoverOpen
      };
    });
  };
  onEndDatePopoverClose = () => {
    this.setState({
      isEndDatePopoverOpen: false
    });
  };
  onRefreshChange = ({
    refreshInterval,
    isPaused
  }) => {
    this.stopInterval();

    if (!isPaused) {
      this.startInterval(refreshInterval);
    }

    if (this.props.onRefreshChange) {
      this.props.onRefreshChange({
        refreshInterval,
        isPaused
      });
    }
  };
  stopInterval = () => {
    if (this.asyncInterval) {
      this.asyncInterval.stop();
    }
  };
  startInterval = refreshInterval => {
    const {
      onRefresh
    } = this.props;

    if (onRefresh) {
      const handler = () => {
        const {
          start,
          end
        } = this.props;
        onRefresh({
          start,
          end,
          refreshInterval
        });
      };

      this.asyncInterval = new AsyncInterval(handler, refreshInterval);
    }
  };
  renderDatePickerRange = () => {
    const {
      end,
      hasChanged,
      isEndDatePopoverOpen,
      isInvalid,
      isStartDatePopoverOpen,
      showPrettyDuration,
      start
    } = this.state;
    const {
      commonlyUsedRanges,
      dateFormat,
      isAutoRefreshOnly,
      isDisabled,
      isPaused,
      locale,
      refreshInterval,
      timeFormat,
      utcOffset
    } = this.props;

    if (isAutoRefreshOnly) {
      return <EuiDatePickerRange className="euiDatePickerRange--inGroup" iconType={false} isCustom startDateControl={<div />} endDateControl={<div />} readOnly>
          <span className="euiSuperDatePicker__prettyFormat">
            {prettyInterval(Boolean(isPaused), refreshInterval)}
          </span>
        </EuiDatePickerRange>;
    }

    if (showPrettyDuration && !isStartDatePopoverOpen && !isEndDatePopoverOpen) {
      return <EuiDatePickerRange className="euiDatePickerRange--inGroup" iconType={false} isCustom startDateControl={<div />} endDateControl={<div />}>
          <button className={classNames('euiSuperDatePicker__prettyFormat', {
          'euiSuperDatePicker__prettyFormat--disabled': isDisabled
        })} data-test-subj="superDatePickerShowDatesButton" disabled={isDisabled} onClick={this.hidePrettyDuration}>
            {prettyDuration(start, end, commonlyUsedRanges, dateFormat)}
            <span className="euiSuperDatePicker__prettyFormatLink">
              <EuiI18n token="euiSuperDatePicker.showDatesButtonLabel" default="Show dates" />
            </span>
          </button>
        </EuiDatePickerRange>;
    }

    return <EuiI18nConsumer>
        {({
        locale: contextLocale
      }) => <EuiDatePickerRange className="euiDatePickerRange--inGroup" iconType={false} isCustom startDateControl={<EuiDatePopoverButton className="euiSuperDatePicker__startPopoverButton" position="start" needsUpdating={hasChanged} isInvalid={isInvalid} isDisabled={isDisabled} onChange={this.setStart} value={start} dateFormat={dateFormat} utcOffset={utcOffset} timeFormat={timeFormat} locale={locale || contextLocale} isOpen={this.state.isStartDatePopoverOpen} onPopoverToggle={this.onStartDatePopoverToggle} onPopoverClose={this.onStartDatePopoverClose} />} endDateControl={<EuiDatePopoverButton position="end" needsUpdating={hasChanged} isInvalid={isInvalid} isDisabled={isDisabled} onChange={this.setEnd} value={end} dateFormat={dateFormat} utcOffset={utcOffset} timeFormat={timeFormat} locale={locale || contextLocale} roundUp isOpen={this.state.isEndDatePopoverOpen} onPopoverToggle={this.onEndDatePopoverToggle} onPopoverClose={this.onEndDatePopoverClose} />} />}
      </EuiI18nConsumer>;
  };
  handleClickUpdateButton = () => {
    if (!this.state.hasChanged && this.props.onRefresh) {
      const {
        start,
        end,
        refreshInterval
      } = this.props;
      this.props.onRefresh({
        start,
        end,
        refreshInterval
      });
    } else {
      this.applyTime();
    }
  };
  renderUpdateButton = () => {
    if (!this.props.showUpdateButton || this.props.isAutoRefreshOnly) {
      return;
    }

    return <EuiFlexItem grow={false}>
        <EuiSuperUpdateButton needsUpdate={this.state.hasChanged} showTooltip={!this.state.isStartDatePopoverOpen && !this.state.isEndDatePopoverOpen} isLoading={this.props.isLoading} isDisabled={this.props.isDisabled || this.state.isInvalid} onClick={this.handleClickUpdateButton} data-test-subj="superDatePickerApplyTimeButton" />
      </EuiFlexItem>;
  };

  render() {
    const {
      commonlyUsedRanges,
      customQuickSelectPanels,
      dateFormat,
      end,
      isAutoRefreshOnly,
      isDisabled,
      isPaused,
      onRefreshChange,
      recentlyUsedRanges,
      refreshInterval,
      showUpdateButton,
      start
    } = this.props;
    const quickSelect = <EuiQuickSelectPopover applyRefreshInterval={onRefreshChange ? this.onRefreshChange : undefined} applyTime={this.applyQuickTime} commonlyUsedRanges={commonlyUsedRanges} customQuickSelectPanels={customQuickSelectPanels} dateFormat={dateFormat} end={end} isAutoRefreshOnly={isAutoRefreshOnly} isDisabled={isDisabled} isPaused={isPaused} recentlyUsedRanges={recentlyUsedRanges} refreshInterval={refreshInterval} start={start} />;
    const flexWrapperClasses = classNames('euiSuperDatePicker__flexWrapper', {
      'euiSuperDatePicker__flexWrapper--noUpdateButton': !showUpdateButton,
      'euiSuperDatePicker__flexWrapper--isAutoRefreshOnly': isAutoRefreshOnly
    });
    return <EuiFlexGroup gutterSize="s" responsive={false} className={flexWrapperClasses}>
        <EuiFlexItem>
          <EuiFormControlLayout className="euiSuperDatePicker" isDisabled={isDisabled} prepend={quickSelect}>
            {this.renderDatePickerRange()}
          </EuiFormControlLayout>
        </EuiFlexItem>
        {this.renderUpdateButton()}
      </EuiFlexGroup>;
  }

}
EuiSuperDatePicker.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  commonlyUsedRanges: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,
    label: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired
  }).isRequired).isRequired,
  customQuickSelectPanels: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.element.isRequired
  }).isRequired),

  /**
     * Specifies the formatted used when displaying dates and/or datetimes
     */
  dateFormat: PropTypes.string.isRequired,
  end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,

  /**
     * Set isAutoRefreshOnly to true to limit the component to only display auto refresh content.
     */
  isAutoRefreshOnly: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isPaused: PropTypes.bool.isRequired,

  /**
     * Used to localize e.g. month names, passed to `moment`
     */
  locale: PropTypes.any,

  /**
     * Callback for when the refresh interval is fired.
     * EuiSuperDatePicker will only manage a refresh interval timer when onRefresh callback is supplied
     * If a promise is returned, the next refresh interval will not start until the promise has resolved.
     * If the promise rejects the refresh interval will stop and the error thrown
     */
  onRefresh: PropTypes.func,

  /**
     * Callback for when the refresh interval changes.
     * Supply onRefreshChange to show refresh interval inputs in quick select popover
     */
  onRefreshChange: PropTypes.func,

  /**
     * Callback for when the time changes.
     */
  onTimeChange: PropTypes.func.isRequired,
  recentlyUsedRanges: PropTypes.arrayOf(PropTypes.shape({
    end: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,
    label: PropTypes.string,
    start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired
  }).isRequired).isRequired,

  /**
     * Refresh interval in milliseconds
     */
  refreshInterval: PropTypes.number.isRequired,

  /**
     * Set showUpdateButton to false to immediately invoke onTimeChange for all start and end changes.
     */
  showUpdateButton: PropTypes.bool.isRequired,
  start: PropTypes.oneOfType([PropTypes.oneOf(["now"]), PropTypes.string.isRequired]).isRequired,

  /**
     * Specifies the formatted used when displaying times
     */
  timeFormat: PropTypes.string.isRequired,
  utcOffset: PropTypes.number
};

try {
  // @ts-ignore
  EuiSuperDatePicker.displayName = "EuiSuperDatePicker"; // @ts-ignore

  EuiSuperDatePicker.__docgenInfo = {
    "description": "",
    "displayName": "EuiSuperDatePicker",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "commonlyUsedRanges": {
        "defaultValue": null,
        "description": "",
        "name": "commonlyUsedRanges",
        "required": false,
        "type": {
          "name": "DurationRange[]"
        }
      },
      "customQuickSelectPanels": {
        "defaultValue": null,
        "description": "",
        "name": "customQuickSelectPanels",
        "required": false,
        "type": {
          "name": "QuickSelectPanel[] | undefined"
        }
      },
      "dateFormat": {
        "defaultValue": {
          value: "MMM D, YYYY @ HH:mm:ss.SSS"
        },
        "description": "Specifies the formatted used when displaying dates and/or datetimes",
        "name": "dateFormat",
        "required": false,
        "type": {
          "name": "string"
        }
      },
      "end": {
        "defaultValue": {
          value: "now"
        },
        "description": "",
        "name": "end",
        "required": false,
        "type": {
          "name": "string"
        }
      },
      "isAutoRefreshOnly": {
        "defaultValue": {
          value: false
        },
        "description": "Set isAutoRefreshOnly to true to limit the component to only display auto refresh content.",
        "name": "isAutoRefreshOnly",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "isDisabled": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isDisabled",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "isLoading": {
        "defaultValue": null,
        "description": "",
        "name": "isLoading",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isPaused": {
        "defaultValue": {
          value: true
        },
        "description": "",
        "name": "isPaused",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "locale": {
        "defaultValue": null,
        "description": "Used to localize e.g. month names, passed to `moment`",
        "name": "locale",
        "required": false,
        "type": {
          "name": "string | boolean | Moment | Duration | string[] | undefined"
        }
      },
      "onRefresh": {
        "defaultValue": null,
        "description": "Callback for when the refresh interval is fired.\nEuiSuperDatePicker will only manage a refresh interval timer when onRefresh callback is supplied\nIf a promise is returned, the next refresh interval will not start until the promise has resolved.\nIf the promise rejects the refresh interval will stop and the error thrown",
        "name": "onRefresh",
        "required": false,
        "type": {
          "name": "((props: OnRefreshProps) => void) | undefined"
        }
      },
      "onRefreshChange": {
        "defaultValue": null,
        "description": "Callback for when the refresh interval changes.\nSupply onRefreshChange to show refresh interval inputs in quick select popover",
        "name": "onRefreshChange",
        "required": false,
        "type": {
          "name": "ApplyRefreshInterval | undefined"
        }
      },
      "onTimeChange": {
        "defaultValue": null,
        "description": "Callback for when the time changes.",
        "name": "onTimeChange",
        "required": true,
        "type": {
          "name": "(props: OnTimeChangeProps) => void"
        }
      },
      "recentlyUsedRanges": {
        "defaultValue": {
          value: "[]"
        },
        "description": "",
        "name": "recentlyUsedRanges",
        "required": false,
        "type": {
          "name": "DurationRange[]"
        }
      },
      "refreshInterval": {
        "defaultValue": {
          value: 0
        },
        "description": "Refresh interval in milliseconds",
        "name": "refreshInterval",
        "required": false,
        "type": {
          "name": "number"
        }
      },
      "showUpdateButton": {
        "defaultValue": {
          value: true
        },
        "description": "Set showUpdateButton to false to immediately invoke onTimeChange for all start and end changes.",
        "name": "showUpdateButton",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "start": {
        "defaultValue": {
          value: "now-15m"
        },
        "description": "",
        "name": "start",
        "required": false,
        "type": {
          "name": "string"
        }
      },
      "timeFormat": {
        "defaultValue": {
          value: "HH:mm"
        },
        "description": "Specifies the formatted used when displaying times",
        "name": "timeFormat",
        "required": false,
        "type": {
          "name": "string"
        }
      },
      "utcOffset": {
        "defaultValue": null,
        "description": "",
        "name": "utcOffset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/super_date_picker/super_date_picker.tsx#EuiSuperDatePicker"] = {
      docgenInfo: EuiSuperDatePicker.__docgenInfo,
      name: "EuiSuperDatePicker",
      path: "src/components/date_picker/super_date_picker/super_date_picker.tsx#EuiSuperDatePicker"
    };
} catch (__react_docgen_typescript_loader_error) {}